import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select, Textarea } from "@/components/ui/Primitives";
import { Video, Plus, ExternalLink, Sparkles, Trash2, BarChart3, Copy, Building2, Play } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

export default function Tours() {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [marketingOpen, setMarketingOpen] = useState(false);
  const [marketingData, setMarketingData] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [activeTour, setActiveTour] = useState(null);
  const [form, setForm] = useState({
    video_url: "", title: "", property_id: "", description_seed: "", visibility: "public"
  });
  const [sourceMode, setSourceMode] = useState("url"); // "url" | "file"
  const [videoFile, setVideoFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        api.get("/tours").catch(() => ({ data: [] })),
        api.get("/properties").catch(() => ({ data: [] })),
      ]);
      setItems(Array.isArray(t.data) ? t.data : (t.data?.tours || []));
      setProperties(Array.isArray(p.data) ? p.data : (p.data?.properties || []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sourceMode === "file" && !videoFile) {
      toast.error("Choose a video file to upload");
      return;
    }
    setSaving(true);
    try {
      let videoUrl = form.video_url.trim();
      if (sourceMode === "file") {
        setUploadPct(0);
        videoUrl = await uploadVideoFile(videoFile, setUploadPct);
      }
      await api.post("/tours", {
        video_url: videoUrl,
        title: form.title || null,
        property_id: form.property_id || null,
        description_seed: form.description_seed || "",
        visibility: form.visibility,
      });
      toast.success("Tour published");
      setOpen(false);
      setForm({ video_url: "", title: "", property_id: "", description_seed: "", visibility: "public" });
      setVideoFile(null);
      setSourceMode("url");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not create tour");
    } finally { setSaving(false); setUploadPct(null); }
  };

  const generateMarketing = async (tour) => {
    try {
      toast.loading("Generating marketing copy…", { id: "mk" });
      const { data } = await api.post(`/tours/${tour.tour_id}/marketing-pack`, {}, { timeout: 45000 });
      toast.dismiss("mk");
      if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
        toast.error("No marketing copy returned. Try again in a moment.");
        return;
      }
      toast.success("Marketing pack ready");
      setMarketingData({ tour, ...data });
      setMarketingOpen(true);
    } catch (err) {
      toast.dismiss("mk");
      const msg = err?.code === "ECONNABORTED"
        ? "Generation timed out. Try again in a moment."
        : (err?.response?.data?.detail || "Generation failed");
      toast.error(msg);
    }
  };

  const viewAnalytics = async (tour) => {
    try {
      const { data } = await api.get(`/tours/${tour.tour_id}/analytics`);
      setAnalyticsData({ tour, ...data });
      setAnalyticsOpen(true);
    } catch (err) {
      toast.error("Analytics unavailable");
    }
  };

  const remove = async (id) => {
    const target = items.find((t) => t.tour_id === id);
    const ok = await confirmDialog({
      title: "Delete this tour?",
      description: `${target?.title || "This tour"} will be removed permanently.\n\nThis cannot be undone.`,
      confirmLabel: "Yes, delete",
      cancelLabel: "Keep tour",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tours/${id}`);
      toast.success("Tour removed");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const copyLink = (tour) => {
    const slug = tour.slug || tour.tour_id;
    const url = `${window.location.origin}/p/${tour.property_id || ""}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  };

  const filtered = filter === "all" ? items : items.filter((t) => t.property_id === filter);

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Virtual tours"
        subtitle="Publish video walkthroughs and attach them to any property listing."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-tour-button"><Plus className="w-4 h-4"/> Add tour</Button>}
      />

      {properties.length > 0 && (
        <div className="mb-5 flex items-center gap-2 flex-wrap">
          <button onClick={() => setFilter("all")} data-testid="tour-filter-all"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === "all" ? "border-green-800 bg-green-50 text-green-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            All ({items.length})
          </button>
          {properties.slice(0, 6).map((p) => {
            const count = items.filter((t) => t.property_id === p.property_id).length;
            return (
              <button key={p.property_id} onClick={() => setFilter(p.property_id)} data-testid={`tour-filter-${p.property_id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === p.property_id ? "border-green-800 bg-green-50 text-green-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                <Building2 className="w-3 h-3 inline mr-1"/> {p.name} ({count})
              </button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={Video}
          title={filter === "all" ? "No tours yet" : "No tours for this property"}
          description="Paste a YouTube, Vimeo, or self-hosted video URL — we'll generate marketing copy automatically."
          action={null}
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((t) => {
            const linkedProperty = properties.find((p) => p.property_id === t.property_id);
            return (
              <Card key={t.tour_id} className="overflow-hidden card-lift" data-testid={`tour-card-${t.tour_id}`}>
                <button onClick={() => { setActiveTour(t); setPlayerOpen(true); }} className="aspect-video bg-slate-900 w-full flex items-center justify-center text-white relative group overflow-hidden">
                  <Play className="w-12 h-12 fill-current group-hover:scale-110 transition-transform"/>
                  <Sparkles className="absolute top-2 left-2 w-3.5 h-3.5 text-lime-300"/>
                </button>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-slate-900 truncate">{t.title || "Untitled tour"}</div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{formatDate(t.created_at)}</span>
                        {linkedProperty && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3"/> {linkedProperty.name}</span>}
                      </div>
                    </div>
                    <Pill tone={t.visibility === "public" ? "green" : "slate"}>{t.visibility}</Pill>
                  </div>
                  <div className="mt-3 flex items-center gap-1 flex-wrap">
                    <Button variant="ghost" onClick={() => generateMarketing(t)} className="!px-2 !py-1.5 text-xs" data-testid={`tour-ai-${t.tour_id}`}>
                      <Sparkles className="w-3.5 h-3.5"/> Marketing
                    </Button>
                    <Button variant="ghost" onClick={() => viewAnalytics(t)} className="!px-2 !py-1.5 text-xs" data-testid={`tour-analytics-${t.tour_id}`}>
                      <BarChart3 className="w-3.5 h-3.5"/> Stats
                    </Button>
                    {linkedProperty && (
                      <Button variant="ghost" onClick={() => copyLink(t)} className="!px-2 !py-1.5 text-xs" data-testid={`tour-copy-${t.tour_id}`}>
                        <Copy className="w-3.5 h-3.5"/>
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => remove(t.tour_id)} className="!px-2 !py-1.5 text-xs text-red-600 hover:bg-red-50 ml-auto" data-testid={`tour-delete-${t.tour_id}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add a virtual tour" testid="tour-modal">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1.5">Video source</div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setSourceMode("url")} data-testid="tour-mode-url"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${sourceMode === "url" ? "border-green-800 bg-green-50 text-green-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                Paste a URL
              </button>
              <button type="button" onClick={() => setSourceMode("file")} data-testid="tour-mode-file"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${sourceMode === "file" ? "border-green-800 bg-green-50 text-green-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                Upload a video file
              </button>
            </div>
          </div>
          {sourceMode === "url" ? (
            <Input label="Video URL" required placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…" value={form.video_url} onChange={(e)=>setForm({...form, video_url:e.target.value})} testid="tour-url-input"/>
          ) : (
            <div>
              <div className="text-xs font-semibold text-slate-600 mb-1.5">Video file (MP4, MOV or WebM — up to 300 MB)</div>
              <input type="file" accept="video/*" data-testid="tour-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f && f.size > 300 * 1024 * 1024) {
                    toast.error("File too large — 300 MB max");
                    e.target.value = "";
                    return;
                  }
                  setVideoFile(f);
                }}
                className="block w-full text-sm text-slate-600 border border-slate-200 rounded-md cursor-pointer file:mr-3 file:px-4 file:py-2 file:rounded-md file:border-0 file:bg-emerald-50 file:text-emerald-700 file:font-semibold file:text-sm hover:file:bg-emerald-100"/>
              {videoFile && (
                <div className="mt-2 text-xs text-slate-600" data-testid="tour-file-meta">
                  {videoFile.name} · {(videoFile.size / (1024 * 1024)).toFixed(1)} MB
                </div>
              )}
              {uploadPct != null && (
                <div className="mt-3">
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${uploadPct}%` }} data-testid="tour-upload-progress"/>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">{uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Processing…"}</div>
                </div>
              )}
            </div>
          )}
          <Input label="Title" placeholder="3-bedroom walk-up near Roncesvalles" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})}/>
          {properties.length > 0 && (
            <Select label="Attach to property" value={form.property_id} onChange={(e)=>setForm({...form, property_id:e.target.value})} testid="tour-property-select">
              <option value="">— Standalone (no property) —</option>
              {properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
            </Select>
          )}
          <Textarea label="Description seed" placeholder="Renovated kitchen, new appliances, dog park nearby…" value={form.description_seed} onChange={(e)=>setForm({...form, description_seed:e.target.value})}/>
          <Select label="Visibility" value={form.visibility} onChange={(e)=>setForm({...form, visibility:e.target.value})}>
            <option value="public">Public — show on listing</option>
            <option value="unlisted">Unlisted — only via link</option>
            <option value="private">Private — only me</option>
          </Select>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="tour-create-submit">
              {saving ? (uploadPct != null && uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Publishing…") : "Publish tour"}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={playerOpen} onClose={() => setPlayerOpen(false)} title={activeTour?.title || "Tour preview"} testid="tour-player-modal">
        {activeTour && (
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            {/youtube|youtu\.be/.test(activeTour.video_url) ? (
              <iframe src={`https://www.youtube.com/embed/${extractYouTubeId(activeTour.video_url)}?autoplay=1`} className="w-full h-full" frameBorder="0" allow="autoplay; encrypted-media; fullscreen" allowFullScreen/>
            ) : /vimeo/.test(activeTour.video_url) ? (
              <iframe src={`https://player.vimeo.com/video/${activeTour.video_url.split("/").pop()}`} className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen/>
            ) : (
              <video src={activeTour.video_url} controls autoPlay className="w-full h-full"/>
            )}
          </div>
        )}
      </Modal>

      <Modal open={marketingOpen} onClose={() => setMarketingOpen(false)} title="Marketing pack" testid="marketing-pack-modal">
        {marketingData && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {marketingData.headline && (
              <Section label="Headline">
                <p className="font-display text-xl font-bold text-slate-900">{marketingData.headline}</p>
              </Section>
            )}
            {marketingData.description && (
              <Section label="Long description">
                <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{marketingData.description}</p>
              </Section>
            )}
            {marketingData.bullets?.length > 0 && (
              <Section label="Key bullets">
                <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
                  {marketingData.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </Section>
            )}
            {marketingData.captions?.length > 0 && (
              <Section label="Social captions">
                {marketingData.captions.map((c, i) => (
                  <div key={i} className="text-sm bg-slate-50 border border-slate-200 rounded p-3 mb-2">
                    <button onClick={() => navigator.clipboard.writeText(c).then(() => toast.success("Copied"))} className="text-[10px] uppercase font-bold text-green-800 mb-1.5">Copy</button>
                    <p className="whitespace-pre-line">{c}</p>
                  </div>
                ))}
              </Section>
            )}
            {!marketingData.headline && !marketingData.description && (
              <pre className="text-xs bg-slate-50 p-3 rounded max-h-96 overflow-auto">{JSON.stringify(marketingData, null, 2)}</pre>
            )}
          </div>
        )}
      </Modal>

      <Modal open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} title="Tour analytics" testid="tour-analytics-modal">
        {analyticsData && (
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Views" value={analyticsData.views ?? analyticsData.view_count ?? 0}/>
            <Stat label="Unique viewers" value={analyticsData.unique_viewers ?? analyticsData.unique ?? "—"}/>
            <Stat label="Applications" value={analyticsData.application_count ?? 0}/>
            <Stat label="Avg watch time" value={analyticsData.avg_watch_seconds ? `${Math.round(analyticsData.avg_watch_seconds)}s` : "—"}/>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{label}</div>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

// Chunked upload to the LOCAL backend (same-origin /api), which assembles
// the parts and pushes the video to persistent object storage. 4 MB chunks
// keep each request under the platform proxy body-size limit.
async function uploadVideoFile(file, onPct) {
  const base = `${window.location.origin}/api`;
  const initRes = await fetch(`${base}/uploads/video/init`, { method: "POST" });
  if (!initRes.ok) throw new Error("Could not start the upload");
  const { upload_id } = await initRes.json();
  const CHUNK = 4 * 1024 * 1024;
  const total = Math.ceil(file.size / CHUNK) || 1;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", upload_id);
    fd.append("index", String(i));
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
    const r = await fetch(`${base}/uploads/video/chunk`, { method: "POST", body: fd });
    if (!r.ok) throw new Error(`Upload failed on part ${i + 1} of ${total}`);
    onPct(Math.min(95, Math.round(((i + 1) / total) * 95)));
  }
  const done = await fetch(`${base}/uploads/video/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ upload_id, filename: file.name, content_type: file.type || "video/mp4" }),
  });
  if (!done.ok) {
    let detail = "Could not finalize the upload";
    try { detail = (await done.json())?.detail || detail; } catch {}
    throw new Error(detail);
  }
  const data = await done.json();
  onPct(100);
  return `${window.location.origin}${data.url}`;
}

function extractYouTubeId(url) {
  const re = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/;
  const m = url.match(re);
  return m ? m[1] : "";
}
